import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import Entity from "../../core/entity/Entity";
import { PhysicsEvents } from "../../core/entity/PhysicsEvents";
import {
  createRegularPolygon,
  PolygonShape,
} from "../../core/util/PolygonUtils";
import { rDirection } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import Asteroid from "../asteroids/Asteroid";
import { DustExplosionEffect } from "../DustExplosionEffect";
import BrokenExpandEffect from "../effects/BrokenExpandEffect";
import { BaseEnemy } from "../enemies/BaseEnemy";
import VectorSprite from "../graphics/VectorSprite";
import { HitType, isHittable } from "../Hittable";
import { LevelEntity } from "../LevelEntity";

// Bullet constants
const DEFAULT_BULLET_LIFETIME = 3.0; // seconds
const DEFAULT_BULLET_DAMAGE = 1;

// Bullet impact shape constants
const BULLET_IMPACT_SIZE = 3;
const BULLET_IMPACT_SIDES = 6;

/**
 * Create a small bullet impact shape - a hexagon with random rotation
 */
function createBulletImpactShape(color: number): PolygonShape {
  // Add random rotation offset
  const rotationOffset = rDirection();

  return {
    points: createRegularPolygon(
      BULLET_IMPACT_SIDES,
      BULLET_IMPACT_SIZE,
      rotationOffset,
    ),
    color: color,
    secondaryColor: color, // Same color for both
  };
}
const DEFAULT_BULLET_SPEED = 300; // pixels per second
const BULLET_MASS = 0.03; // physics mass
const BULLET_COLLISION_RADIUS = 2; // collision detection radius
const BULLET_VISUAL_HALF_WIDTH = 6; // half width of bullet visual sprite
const BULLET_STROKE_WIDTH = 1; // stroke width for bullet sprite

// Bullet impact explosion constants
const BULLET_IMPACT_LIFETIME = 0.2;
const BULLET_IMPACT_VELOCITY_SCALE = 25;

// Dust explosion constants
const DUST_PARTICLE_COUNT = 5;
const DUST_EXPLOSION_SPEED = 40;

/**
 * Get the primary color of a target entity for dust explosion effects
 */
function getTargetColor(target: unknown): number {
  if (target instanceof BaseEnemy) {
    return target.config.visualConfig.color;
  }
  if (target instanceof Asteroid) {
    return Colors.WHITE; // Asteroids use white color
  }
  return Colors.ORANGE; // Default fallback color
}

export default class Bullet extends LevelEntity implements Entity {
  private renderer: VectorSprite;
  lifetime: number = DEFAULT_BULLET_LIFETIME;
  damage: number = DEFAULT_BULLET_DAMAGE;

  constructor(
    position: V2d,
    direction: V2d,
    speed: number = DEFAULT_BULLET_SPEED,
    damage: number = DEFAULT_BULLET_DAMAGE,
  ) {
    super();

    // Create line segment renderer for bullet visual
    this.renderer = VectorSprite.fromSegments([
      {
        start: V(-BULLET_VISUAL_HALF_WIDTH, 0),
        end: V(BULLET_VISUAL_HALF_WIDTH, 0),
        color: Colors.YELLOW,
        thickness: BULLET_STROKE_WIDTH,
      },
    ]);
    this.addChild(this.renderer);

    this.body = new Body({ mass: BULLET_MASS });
    const shape = new Circle({ radius: BULLET_COLLISION_RADIUS });
    shape.collisionGroup = collisionGroups("Bullet");
    shape.collisionMask = collisionGroups("Enemy", "Bomb");
    this.body.addShape(shape);
    this.body.position = [position.x, position.y];

    const velocity = direction.normalize().imul(speed);
    this.body.velocity = velocity;
    this.damage = damage;
  }

  onTick(dt: number) {
    this.lifetime -= dt;
    if (this.lifetime <= 0) {
      this.destroy();
    }
  }

  onRender(dt: number) {
    if (this.body && this.renderer) {
      this.renderer.setPosition(this.body.position);

      // Rotate sprite to match bullet direction
      const velocity = V(this.body.velocity);
      this.renderer.setRotation(velocity.angle);
    }
  }

  onBeginContact({ other }: PhysicsEvents["beginContact"]) {
    if (!this.body) return;

    if (isHittable(other)) {
      // Dispatch explosion event for sound effects
      this.dispatch("explosion", {
        explosionType: "bulletImpact" as const,
        position: { x: this.body.position[0], y: this.body.position[1] },
        radius: 5, // Small bullet impact
        lifetime: BULLET_IMPACT_LIFETIME,
      });

      // Create bullet impact explosion using bullet color (no velocity inheritance for impacts)
      const impactShape = createBulletImpactShape(Colors.YELLOW);
      this.getCurrentLevel()?.addChild(
        new BrokenExpandEffect({
          position: V(this.body.position),
          polygon: impactShape.points,
          color: impactShape.color,
          lifetime: BULLET_IMPACT_LIFETIME,
          velocityScale: BULLET_IMPACT_VELOCITY_SCALE,
        }),
      );

      // Create dust explosion using both bullet and target colors
      const bulletColor = Colors.YELLOW; // Current bullet color
      const targetColor = getTargetColor(other);
      const dustExplosion = new DustExplosionEffect({
        position: V(this.body.position),
        colors: [bulletColor, targetColor],
        particleCount: DUST_PARTICLE_COUNT,
        explosionSpeed: DUST_EXPLOSION_SPEED,
      });

      // Add dust explosion to the current level
      this.getCurrentLevel()?.addChild(dustExplosion);

      // Hit the target and destroy the bullet
      other.takeHit(this.damage, HitType.Bullet);
      this.destroy();
    }
  }
}
